/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BEM, useToggle } from "@adapptio/lib-components";
import { RoutesEditor } from "../../RoutesEditor";
import { IRouteListProps } from "../../../../../ui/components/RouteList";
import {
	ITableColumn,
	ITableSortConfig,
	Table,
	TABLE_SORT,
	TableButton,
	TableCol,
	TableRow,
	useTable
} from "../../../../../ui/components/Table";
import { sortTableData } from "../../../../../ui/components/Table/TableUtil";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { InspectorControlString } from "../../../../../inspector/controls/InspectorControlString";
import { TGetBlueprintSchemaModel, ValidatorString } from "@adapptio/lib-blueprint";
import { TBlueprintRoutesSchema } from "@adapptio/lib-core";
import { FieldGroup, FieldText } from "@adapptio/lib-editor-ui";
import { useFieldState } from "../../../../../ui/hooks";
import { Key } from "ts-key-enum";
import { generateId } from "@adapptio/lib-components/src/Functions/generateId";

export interface IRoutesEditorMainProps {
	className?: string;
	view: RoutesEditor;
}

const routeListColumns: ITableColumn[] = [
	{
		id: "key",
		// @todo Translate
		label: "Key",
		sortable: true,
		headerAlign: "left",
		width: "30%"
	},
	{
		id: "label",
		// @todo Translate
		label: "Label",
		sortable: true,
		headerAlign: "left"
	},
	{
		id: "path",
		// @todo Translate
		label: "Path",
		sortable: true,
		headerAlign: "left"
	},
	{
		id: "action",
		// @todo Translate
		label: "",
		width: "120px",
		sortable: false,
		headerAlign: "left"
	}
];

const routeListDefaultSort: ITableSortConfig[] = [
	{
		columnId: "path",
		direction: TABLE_SORT.ASC
	}
];

export const RoutesEditorMain: React.FC<IRoutesEditorMainProps> = ({ className, view }) => {
	const baseClass = BEM("edt-routes-editor-main");
	const [ selectedRouteKey, setSelectedRouteKey ] = useState("");
	const [ sortingPrevented, , preventSorting, allowSorting ] = useToggle(false);
	const prevSortedItems =
		useRef<TGetBlueprintSchemaModel<TBlueprintRoutesSchema>["props"]["spec"]["items"]>();

	const routesModel = view.getRoutesModel();

	const [ items, setItems ] = useState(() => routesModel?.getItems());

	const [ fieldState, setFieldState, fieldGroup ] = useFieldState(
		{
			label: {
				defaultValue: "",
				validators: [ ValidatorString({ required: true, min: 1 }) ]
			},
			path: {
				defaultValue: "",
				validators: [ ValidatorString({ required: true, min: 1 }) ]
			}
		},
		[ items ]
	);

	const [ tableProps ] = useTable({
		columns: routeListColumns,
		preventMultisort: true,
		defaultState: {
			selectionIds: [ selectedRouteKey ],
			sort: routeListDefaultSort
		}
	});

	const filteredAndSortedItems = useMemo(() => {
		if (!sortingPrevented) {
			prevSortedItems.current = sortTableData(items, tableProps.state.sort, (item, columnId) => {
				switch (columnId) {
					case "key":
						return item.key.value;
					case "label":
						return item.value.props.label.value;
					case "path":
						return item.value.props.path.value;
					default:
						return null;
				}
			});
		}

		return prevSortedItems.current;
	}, [ items, tableProps.state.sort, sortingPrevented ]);

	const handleRouteSelect = useCallback<IRouteListProps["onSelect"]>((routeKey) => {
		setSelectedRouteKey(routeKey);
	}, []);

	const addRoute = useCallback(() => {
		const id = generateId();
		const label = fieldState.label.value;
		const path = fieldState.path.value;

		routesModel.addNewItem(id, { path, label });

		fieldGroup.reset();
	}, [ routesModel, fieldGroup, fieldState ]);

	const getDeleteRow = useCallback(
		(item: TGetBlueprintSchemaModel<TBlueprintRoutesSchema>["props"]["spec"]["items"][number]) => () => {
			routesModel.removeItem(item.key);
		},
		[]
	);

	const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
		(event) => {
			if (event.key === Key.Enter) {
				if (fieldGroup.valid && !fieldGroup.pristine) {
					addRoute();
				}
			}
		},
		[ addRoute, fieldGroup ]
	);

	useEffect(() => {
		const onRouteListChange = () => {
			setItems([ ...routesModel.getItems() ]);
		};

		onEvent(routesModel.onModelChange, onRouteListChange);

		return () => {
			offEvent(routesModel.onModelChange, onRouteListChange);
		};
	}, [ routesModel ]);

	useEffect(() => {
		if (!tableProps.state.selectionIds.includes(selectedRouteKey)) {
			tableProps.onRowClick(selectedRouteKey);
		}
	}, [ selectedRouteKey ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("container").toClassName()}>
				<Table {...tableProps} onRowClick={handleRouteSelect}>
					<TableRow id="new">
						<TableCol>
							<FieldGroup grow>
								<FieldText value={""} readOnly grow placeholder="Auto-generated" />
							</FieldGroup>
						</TableCol>
						<TableCol>
							<FieldGroup grow>
								<FieldText
									value={fieldState.label.value}
									onChange={setFieldState.label}
									validationErrors={fieldState.label.validationErrors}
									grow
									placeholder="My Route"
									onKeyDown={handleKeyDown}
								/>
							</FieldGroup>
						</TableCol>
						<TableCol>
							<FieldGroup grow>
								<FieldText
									value={fieldState.path.value}
									onChange={setFieldState.path}
									validationErrors={fieldState.path.validationErrors}
									grow
									placeholder="/myroute"
									onKeyDown={handleKeyDown}
								/>
							</FieldGroup>
						</TableCol>
						<TableCol>
							<TableButton
								icon="mdi/plus-thick"
								onClick={addRoute}
								tooltip="Add route"
								disabled={!fieldGroup.valid || fieldGroup.pristine}
							/>
						</TableCol>
					</TableRow>
					{filteredAndSortedItems.map((item) => (
						<TableRow id={String(item.key?.value)} key={item.key?.value}>
							<TableCol>
								<InspectorControlString model={item.key} readOnly />
							</TableCol>
							<TableCol>
								<InspectorControlString
									model={item.value?.props?.label}
									onFocus={preventSorting}
									onBlur={allowSorting}
								/>
							</TableCol>
							<TableCol>
								<InspectorControlString
									model={item.value?.props?.path}
									onFocus={preventSorting}
									onBlur={allowSorting}
								/>
							</TableCol>
							<TableCol>
								<TableButton
									icon="mdi/minus-circle"
									onClick={getDeleteRow(item)}
									tooltip="Delete route"
								/>
							</TableCol>
						</TableRow>
					))}
				</Table>
			</div>
		</div>
	);
};
